// +----------------------------------------------------------------------
// | 话题详情 /topics/:key：这一件事，大家最后都怎么选的
// |
// | key 既可以是话题 slug（job-change），也可以是分类（work）。
// |
// | 一个刻意的设计：样本不够时**不画百分比**，只如实报人数，并明确说
// | 「人太少的时候『大多数』这个词没有意义」——这和「想想」产品里
// | 「信息不足禁止出百分比」是同一条规则。
// +----------------------------------------------------------------------
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { topicStats } from '../../api/topic';
import TopicBars from '../../components/TopicBars';
import { toast } from '../../components/Toast';
import './TopicDetail.less';

const DAY_OPTIONS = [
  { value: 7, label: '近 7 天' },
  { value: 30, label: '近 30 天' },
  { value: 90, label: '近 90 天' },
];

export default function TopicDetail() {
  const { key } = useParams();
  const navigate = useNavigate();

  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await topicStats(key, { days });
      setData(res);
    } catch (e) {
      setData(null);
      setNotFound(true);
      toast(e.message || '这个话题没找到', 'error');
    } finally {
      setLoading(false);
    }
  }, [key, days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <div className="page center muted">正在数一数…</div>;
  }

  if (notFound || !data) {
    return (
      <div className="page topic-detail">
        <div className="card topic-detail-empty">
          <strong>没有这个话题</strong>
          <span className="muted">可能是链接过期了，或者这个话题还没进榜。</span>
          <Link to="/topics" className="btn btn-ghost btn-sm">
            ← 回榜单看看
          </Link>
        </div>
      </div>
    );
  }

  const isCategory = data.kind === 'category';
  const available = !!data.percent_available;

  return (
    <div className="page topic-detail">
      <Link to="/topics" className="topic-detail-back muted">
        ← 大家最近在纠结什么
      </Link>

      <header className="topic-detail-head">
        <span className="topic-detail-emoji">{data.emoji}</span>
        <div className="topic-detail-head-main">
          <h1 className="topic-detail-title">{data.label}</h1>
          <div className="topic-detail-tags">
            <span className="tag tag-plain">{isCategory ? '整个分类' : '具体话题'}</span>
            {!isCategory && (
              <button
                type="button"
                className="topic-detail-cat"
                onClick={() => navigate(`/topics/${data.category}`)}
              >
                {data.category_label} ›
              </button>
            )}
          </div>
        </div>
      </header>

      {/* 数字概览 */}
      <div className="card topic-detail-numbers">
        <div className="topic-detail-num">
          <strong>{data.sample}</strong>
          <span className="muted">人想过这件事</span>
        </div>
        <div className="topic-detail-num">
          <strong>{data.decided}</strong>
          <span className="muted">人做出了选择</span>
        </div>
        <div className="topic-detail-num">
          <strong>{available && data.satisfied_percent !== null ? `${data.satisfied_percent}%` : '—'}</strong>
          <span className="muted">
            事后觉得选得还行
            {available && data.satisfied > 0 ? `（${data.satisfied} 人）` : ''}
          </span>
        </div>
      </div>

      <div className="topic-detail-days">
        {DAY_OPTIONS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`topic-detail-day ${days === item.value ? 'is-active' : ''}`}
            onClick={() => setDays(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* 选择分布 */}
      <div className="card topic-detail-options">
        <div className="topic-detail-card-head">
          <strong>{isCategory ? '这类事最后都怎么选的' : '最后都怎么选的'}</strong>
          {available && <span className="tag tag-plain">基于 {data.decided} 个选择</span>}
        </div>

        <TopicBars options={data.options || []} available={available} />

        <p className={`topic-detail-note ${available ? 'muted' : 'is-warning'}`}>{data.note}</p>
      </div>

      {/* 引导回自己的事 */}
      <div className="card topic-detail-cta">
        <strong>别人的比例只是参考</strong>
        <span className="muted">
          你这件纠结的事和他们不一定一样。想清楚自己的那件事，才算数。
        </span>
        <div className="topic-detail-cta-actions">
          <Link to={`/?ask=${encodeURIComponent(data.label)}`} className="btn btn-primary btn-sm">
            我也在纠结这件事
          </Link>
          <Link to="/topics" className="btn btn-ghost btn-sm">
            看看别的
          </Link>
        </div>
      </div>

      {data.window && (
        <p className="muted topic-detail-window">
          统计窗口：{data.window.from} ~ {data.window.to}
        </p>
      )}
    </div>
  );
}
